import dataModel from '../models/DataModel';
import inventoryItemService from '../services/inventoryItemService';
import DAO from './DAO.js';
import Metadata from '../helpers/metadata/Metadata';
import OfflineQueue from './queue/OfflineQueue';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import { task_root } from './TasksDAO';

export default class InventoryItemsDAO extends DAO {

  constructor(store) {
    super(store, 'inventory_item');
    this.OfflineQueue = OfflineQueue;

    this.completeOfflineUpdate = this.completeOfflineUpdate.bind(this);
    this.unsetInventoryItem = this.unsetInventoryItem.bind(this);
    this.clearInventoryItems = this.clearInventoryItems.bind(this);
  }

  unsetInventoryItem(id) {
    StoreItemProcessing.deleteItemInStore(this.itemType, id);
  }

  clearInventoryItems() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  getInventoryItem(itemId, options) {
    return this._getItem(itemId, inventoryItemService.getInventoryItem.bind(this, itemId), options);
  }

  get inventoryItemId() { 
    if (!this._inventoryItemId) {
      this._inventoryItemId = 0;
    }
    return this._inventoryItemId--;
  }

  async createInventoryItem(inventoryItem, taskId, eventMetadatum) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    const response = await inventoryItemService.createInventoryItem(inventoryItem, taskId, eventMetadatum);
    if (response.error && !response.offline) {
      dataModel.displayHTTPError('Updating an inventory item', response.error, false);
    }
    else if (response.offline) {
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      return this.handleOfflineCreate(inventoryItem, task, eventMetadatum);
    }
    else {
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      this.markDirty(inventoryItem, false);
      dataModel.tasksDAO.markDirty(task, false);
      return task;
    }
  }

  async updateInventoryItem(inventoryItem, taskId, eventMetadatum) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    
    const response = await inventoryItemService.updateInventoryItem(inventoryItem, taskId, eventMetadatum);
    if (response.error && !response.offline) {
      dataModel.displayHTTPError('Updating an inventory item', response.error, false);
    }
    else if (response.offline) {
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      return this.handleOfflineUpdate(inventoryItem, task, eventMetadatum);
    }
    else {
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      this.markDirty(inventoryItem, false);
      dataModel.tasksDAO.markDirty(task, false);
      return task;
    }
  }

  async deleteInventoryItem(inventoryItem, taskId, options, eventMetadatum) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    // Not in Offline Mode
    const response = await inventoryItemService.deleteInventoryItem(inventoryItem.id, taskId, eventMetadatum);
    if (response.error && !response.offline) {
      dataModel.displayHTTPError('Deleting an inventory item', response.error, false);
    }
    else if (response.offline) {
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      return this.handleOfflineDelete(inventoryItem, task, eventMetadatum);
    }
    else {
      this.unsetInventoryItem(inventoryItem.id);
      let task = await dataModel.tasksDAO.getTask(taskId, { recordDescriptor: task_root });
      dataModel.tasksDAO.markDirty(task, false);
      this.deleteItemFromTask(inventoryItem, task);
      return task;
    }
  }

  deleteItemFromTask(inventoryItem, task) {
    task.inventory_items = task.inventory_items.filter( 
      item => {
        return item.id != inventoryItem.id;
      }
    );
    dataModel.tasksDAO.normalizeItem(task, { store: true });
  }

  handleOfflineDelete(inventoryItem, task, eventMetadatum) {
    if (inventoryItem) {
      this.unsetInventoryItem(inventoryItem.id);
      
      // delete from task
      if (task){
        this.deleteItemFromTask(inventoryItem, task);
        dataModel.tasksDAO.markDirty(task, true);
      }

      // queue changes
      this.OfflineQueue.addQueueItem('inventoryItem', 'delete', 
        { 
          inventoryItem: Object.assign({}, inventoryItem),
          taskId: task.id,
          eventMetadatum: eventMetadatum,
        }
      );
    }
  }

  handleOfflineCreate(inventoryItem, task, eventMetadatum) {
    if (inventoryItem) {
      if (!task.inventory_items) {
        task.inventory_items = [];
      }
      inventoryItem.id = this.inventoryItemId;

      this.markDirty(inventoryItem, true);
      this.normalizeItem(inventoryItem, { store: true });

      task.inventory_items.push(inventoryItem.id);

      dataModel.tasksDAO.markDirty(task, true);
      const normalizedTask = dataModel.tasksDAO.normalizeItem(task, { store: true });
      this.OfflineQueue.addQueueItem('inventoryItem', 'create',
        {
          inventoryItem: Object.assign({}, inventoryItem),
          taskId: task.id,
          eventMetadatum: eventMetadatum,
        });
      return dataModel.tasksDAO.mapToReturn(normalizedTask, { recordDescriptor: task_root });
    }
    else {
      return null;
    }
  }

  async handleOfflineUpdate(inventoryItem, task, eventMetadatum) {
    if (inventoryItem) {
      this.markDirty(inventoryItem, true);
      if (inventoryItem.inventory_id) {
        let inventory = dataModel.inventoryDAO.findItem('inventories', inventoryItem.inventory_id);
        if (inventory && inventory.name != inventoryItem.name) {
          delete inventoryItem.inventory_id;
          delete inventoryItem.inventory;
        }
      }
      this.normalizeItem(inventoryItem, { store: true });

      dataModel.tasksDAO.markDirty(task, true);
      const normalizedTask = dataModel.tasksDAO.normalizeItem(task, { store: true });
      this.OfflineQueue.addQueueItem('inventoryItem', 'update',
        {
          inventoryItem: Object.assign({}, inventoryItem),
          taskId: task.id,
          eventMetadatum: eventMetadatum,
        });
      return dataModel.tasksDAO.mapToReturn(normalizedTask, { recordDescriptor: task_root });
    }
  }

  /** Util Functions */
  completeOfflineUpdate(queueItem) {
    switch(queueItem.operationKey) {
    case 'delete':
      return this.deleteInventoryItem(queueItem.itemInfo.inventoryItem, queueItem.itemInfo.taskId, queueItem.itemInfo.eventMetadatum);
    case 'create':
      return this.createInventoryItem(queueItem.itemInfo.inventoryItem, queueItem.itemInfo.taskId, queueItem.itemInfo.eventMetadatum);
    case 'update':
      return this.updateInventoryItem(queueItem.itemInfo.inventoryItem, queueItem.itemInfo.taskId, queueItem.itemInfo.eventMetadatum);
    default: 
      break;
    }
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_inventory_items, options.toIgnore.inventoryItems, this.unsetInventoryItem, this.clearInventoryItems, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/InventoryItemsDAO.js